import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    clearDismissedDuplicateAddressCandidates,
    dismissDuplicateAddressCandidate,
    getDuplicateAddressCandidates,
    purgeDeliveriesForHiddenAddress
} from "../api/addresses";
import { useUpdateAddress } from "../api/addresses/hooks";
import type { DuplicateAddressCandidate } from "../api/addresses/types";
import { mapboxAccessToken, mapboxSatelliteStyle, mapInitialView } from "../map/config";
import { showError, showInfo, showSuccess } from "../shared/toast";
import "./hidden-addresses-panel.css";
import "./duplicate-address-candidates.css";

const QUERY_KEY = ["addresses", "duplicate-candidates"];
const LINES_SOURCE_ID = "duplicate-candidate-lines";
const POINTS_SOURCE_ID = "duplicate-candidate-points";

function formatAddressLine(street: string, number?: string | null): string {
    return [street, number].filter(Boolean).join(" ");
}

function pairKey(candidate: DuplicateAddressCandidate): string {
    return [candidate.id1, candidate.id2].sort().join(":");
}

interface PostalGroup {
    postalCode: string;
    city: string;
    rows: DuplicateAddressCandidate[];
}

function groupByPostalCode(rows: DuplicateAddressCandidate[]): PostalGroup[] {
    const groups = new Map<string, PostalGroup>();
    for (const row of rows) {
        const key = row.postalCode1 || "Okänt postnummer";
        let group = groups.get(key);
        if (!group) {
            group = { postalCode: key, city: row.city1, rows: [] };
            groups.set(key, group);
        }
        group.rows.push(row);
    }
    for (const group of groups.values()) {
        group.rows.sort((a, b) => {
            const streetCmp = a.street1.localeCompare(b.street1, "sv");
            if (streetCmp !== 0) {
                return streetCmp;
            }
            return (a.number1 ?? "").localeCompare(b.number1 ?? "", "sv", { numeric: true });
        });
    }
    return Array.from(groups.values()).sort((a, b) => a.postalCode.localeCompare(b.postalCode, "sv"));
}

export const DuplicateAddressCandidatesPage = (): JSX.Element => {
    const queryClient = useQueryClient();
    const updateAddressMutation = useUpdateAddress();
    const purgeMutation = useMutation({ mutationFn: purgeDeliveriesForHiddenAddress });
    const dismissMutation = useMutation({
        mutationFn: ({ id1, id2 }: { id1: string; id2: string }) => dismissDuplicateAddressCandidate(id1, id2)
    });
    const clearDismissedMutation = useMutation({ mutationFn: clearDismissedDuplicateAddressCandidates });
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [mergingKey, setMergingKey] = useState<string | null>(null);
    const [dismissingKey, setDismissingKey] = useState<string | null>(null);
    const [resolvedCount, setResolvedCount] = useState(0);
    const [dismissedCount, setDismissedCount] = useState(0);
    const [selectedPostalCode, setSelectedPostalCode] = useState<string>("all");
    const lastFitScopeRef = useRef<string | null>(null);
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

    // Dismissed pairs are excluded server-side (stored in the DB, not per-browser), so
    // `candidates` here is already the "still active" list.
    const { data: candidates = [], isLoading, error } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: getDuplicateAddressCandidates
    });

    const postalGroups = useMemo(() => groupByPostalCode(candidates), [candidates]);

    const filteredCandidates = useMemo(
        () =>
            selectedPostalCode === "all"
                ? candidates
                : candidates.filter((c) => (c.postalCode1 || "Okänt postnummer") === selectedPostalCode),
        [candidates, selectedPostalCode]
    );

    const displayedGroups = useMemo(() => groupByPostalCode(filteredCandidates), [filteredCandidates]);

    // Only depends on stable mutation/query-client references, so it's safe to capture once
    // inside the map-init effect below (which only runs on mount) without going stale.
    const dismissPair = async (id1: string, id2: string) => {
        const key = [id1, id2].sort().join(":");
        setDismissingKey(key);
        try {
            await dismissMutation.mutateAsync({ id1, id2 });
            setDismissedCount((n) => n + 1);
            showInfo("Markerad som olika adresser");
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte markera som olika adresser", msg);
        } finally {
            setDismissingKey(null);
        }
    };

    // Map container is always rendered (not gated behind isLoading) so this init effect,
    // which only runs once on mount, always finds a real DOM node to attach to.
    useEffect(() => {
        if (mapRef.current || !mapContainerRef.current || !mapboxAccessToken) {
            return;
        }

        mapboxgl.accessToken = mapboxAccessToken;
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapboxSatelliteStyle,
            center: mapInitialView.center,
            zoom: mapInitialView.zoom,
            minZoom: 3,
            maxZoom: 19
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");

        map.once("load", () => {
            map.addSource(LINES_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
            map.addSource(POINTS_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [] } });

            map.addLayer({
                id: LINES_SOURCE_ID,
                type: "line",
                source: LINES_SOURCE_ID,
                paint: {
                    "line-color": ["case", ["get", "selected"], "#d92b2b", "#f0a500"],
                    "line-width": ["case", ["get", "selected"], 4, 2],
                    "line-dasharray": [2, 1]
                }
            });

            map.addLayer({
                id: POINTS_SOURCE_ID,
                type: "circle",
                source: POINTS_SOURCE_ID,
                paint: {
                    "circle-radius": ["case", ["get", "selected"], 8, 6],
                    "circle-color": ["case", ["get", "selected"], "#d92b2b", "#f0a500"],
                    "circle-stroke-color": "#ffffff",
                    "circle-stroke-width": 2
                }
            });

            const selectFromEvent = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
                const key = e.features?.[0]?.properties?.pairKey;
                if (key) {
                    setSelectedKey(key);
                }
            };

            map.on("click", LINES_SOURCE_ID, selectFromEvent);
            map.on("click", POINTS_SOURCE_ID, selectFromEvent);
            map.on("mouseenter", POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = "pointer";
            });
            map.on("mouseleave", POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = "";
            });

            const dismissFromEvent = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => {
                e.preventDefault();
                const props = e.features?.[0]?.properties;
                if (props?.id1 && props?.id2) {
                    void dismissPair(props.id1, props.id2);
                }
            };

            map.on("contextmenu", LINES_SOURCE_ID, dismissFromEvent);
            map.on("contextmenu", POINTS_SOURCE_ID, dismissFromEvent);

            setIsMapReady(true);
        });

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady) {
            return;
        }

        const lineFeatures: GeoJSON.Feature[] = filteredCandidates.map((c) => ({
            type: "Feature",
            properties: { pairKey: pairKey(c), id1: c.id1, id2: c.id2, selected: pairKey(c) === selectedKey },
            geometry: {
                type: "LineString",
                coordinates: [
                    [c.longitude1, c.latitude1],
                    [c.longitude2, c.latitude2]
                ]
            }
        }));

        const pointFeatures: GeoJSON.Feature[] = filteredCandidates.flatMap((c) => {
            const selected = pairKey(c) === selectedKey;
            const properties = { pairKey: pairKey(c), id1: c.id1, id2: c.id2, selected };
            return [
                {
                    type: "Feature",
                    properties,
                    geometry: { type: "Point", coordinates: [c.longitude1, c.latitude1] }
                },
                {
                    type: "Feature",
                    properties,
                    geometry: { type: "Point", coordinates: [c.longitude2, c.latitude2] }
                }
            ];
        });

        (map.getSource(LINES_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features: lineFeatures
        });
        (map.getSource(POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined)?.setData({
            type: "FeatureCollection",
            features: pointFeatures
        });

        if (lastFitScopeRef.current !== selectedPostalCode && filteredCandidates.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            filteredCandidates.forEach((c) => {
                bounds.extend([c.longitude1, c.latitude1]);
                bounds.extend([c.longitude2, c.latitude2]);
            });
            map.fitBounds(bounds, { padding: 60, maxZoom: 18 });
            lastFitScopeRef.current = selectedPostalCode;
        }
    }, [filteredCandidates, selectedKey, isMapReady, selectedPostalCode]);

    useEffect(() => {
        if (!selectedKey) {
            return;
        }
        rowRefs.current.get(selectedKey)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [selectedKey]);

    const flyTo = (candidate: DuplicateAddressCandidate) => {
        setSelectedKey(pairKey(candidate));
        const map = mapRef.current;
        if (!map) {
            showError("Kartan är inte redo än, försök igen om en stund");
            return;
        }
        const midLng = (candidate.longitude1 + candidate.longitude2) / 2;
        const midLat = (candidate.latitude1 + candidate.latitude2) / 2;
        map.flyTo({ center: [midLng, midLat], zoom: 19 });
    };

    // Keeps the topmost row in view auto-selected — on first load, after a postal-code
    // filter change, and after "Behåll denna"/"Inte samma" removes the selected pair and
    // the list reflows — so working the list top-to-bottom never needs an extra "Visa" click.
    useEffect(() => {
        if (!isMapReady || filteredCandidates.length === 0) {
            return;
        }
        const stillVisible = selectedKey !== null && filteredCandidates.some((c) => pairKey(c) === selectedKey);
        if (stillVisible) {
            return;
        }
        const top = displayedGroups[0]?.rows[0];
        if (top) {
            flyTo(top);
        }
    }, [displayedGroups, filteredCandidates, selectedKey, isMapReady]);

    const clearAllDismissed = async () => {
        try {
            const { clearedCount } = await clearDismissedMutation.mutateAsync();
            setDismissedCount(0);
            showSuccess(clearedCount > 0 ? `${clearedCount} dolda par visas igen` : "Inga dolda par att visa");
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte visa dolda par igen", msg);
        }
    };

    const keepAddress = async (candidate: DuplicateAddressCandidate, which: 1 | 2) => {
        const kept = which === 1
            ? formatAddressLine(candidate.street1, candidate.number1)
            : formatAddressLine(candidate.street2, candidate.number2);
        const droppedId = which === 1 ? candidate.id2 : candidate.id1;
        const dropped = which === 1
            ? formatAddressLine(candidate.street2, candidate.number2)
            : formatAddressLine(candidate.street1, candidate.number1);

        const confirmed = window.confirm(
            `Dölj ${dropped} (${candidate.distanceMeters} m bort) och ta bort dess utdelningar? ` +
                `${kept} lämnas orörd.`
        );
        if (!confirmed) {
            return;
        }

        const key = pairKey(candidate);
        setMergingKey(key);
        try {
            await updateAddressMutation.mutateAsync({ id: droppedId, request: { isHidden: true } });
            const { deletedCount } = await purgeMutation.mutateAsync(droppedId);
            showSuccess(
                deletedCount > 0
                    ? `Dolde ${dropped} och tog bort ${deletedCount} utdelning${deletedCount === 1 ? "" : "ar"}`
                    : `Dolde ${dropped}`
            );
            setResolvedCount((n) => n + 1);
            await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        } catch (e) {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError(`Kunde inte dölja ${dropped}`, msg);
        } finally {
            setMergingKey(null);
        }
    };

    if (error) {
        return (
            <div className="duplicate-candidates-page">
                <h2>Möjliga adressdubbletter</h2>
                <p className="error-message">Kunde inte ladda kandidater.</p>
            </div>
        );
    }

    return (
        <section className="duplicate-candidates-page" aria-label="Möjliga adressdubbletter">
            <div className="duplicate-candidates-page__header">
                <h2>Möjliga adressdubbletter</h2>
                <p className="duplicate-candidates-page__description">
                    Adresspar nära varandra på olika gator — t.ex. samma hus registrerat med adress mot två
                    gator i en korsning. Detta är en gissning baserad på avstånd, inte en bekräftad lista:
                    bedöm varje par på kartan innan du slår ihop dem.
                </p>
                <div className="duplicate-candidates-page__stats">
                    {isLoading ? (
                        "Laddar..."
                    ) : (
                        <>
                            <strong>{filteredCandidates.length}</strong> kvar
                            {selectedPostalCode !== "all" && <> i {selectedPostalCode}</>} (av {candidates.length} laddade)
                            {resolvedCount > 0 && <> · {resolvedCount} lösta</>}
                            {dismissedCount > 0 && <> · {dismissedCount} markerade som olika</>}
                            {" "}
                            <button className="action-button" onClick={clearAllDismissed}>
                                Visa dolda par igen
                            </button>
                            <select
                                className="duplicate-candidates-page__postal-select"
                                value={selectedPostalCode}
                                onChange={(e) => setSelectedPostalCode(e.target.value)}
                            >
                                <option value="all">Alla postnummer ({candidates.length})</option>
                                {postalGroups.map((g) => (
                                    <option key={g.postalCode} value={g.postalCode}>
                                        {g.postalCode} {g.city} ({g.rows.length})
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                </div>
            </div>

            <div className="duplicate-candidates-page__layout">
                <div className="duplicate-candidates-page__map" ref={mapContainerRef} />
                <div className="duplicate-candidates-page__list">
                    {!isLoading && filteredCandidates.length === 0 ? (
                        <p className="hidden-addresses-panel__empty">
                            {candidates.length === 0 ? "Inga kandidater hittades." : "Inga kandidater kvar i det här urvalet."}
                        </p>
                    ) : (
                        displayedGroups.map((group) => (
                            <div className="hidden-addresses-panel__group" key={group.postalCode}>
                                <div className="hidden-addresses-panel__group-header">
                                    <strong>{group.postalCode}</strong> {group.city}
                                    <span className="hidden-addresses-panel__group-meta">{group.rows.length} par</span>
                                </div>
                                <table className="hidden-addresses-table">
                                    <thead>
                                        <tr>
                                            <th>Karta</th>
                                            <th>Adress 1</th>
                                            <th>Adress 2</th>
                                            <th>Avstånd</th>
                                            <th>Olika?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.rows.map((c) => {
                                            const key = pairKey(c);
                                            const isMerging = mergingKey === key;
                                            const isDismissing = dismissingKey === key;
                                            const isBusy = isMerging || isDismissing;
                                            return (
                                                <tr
                                                    key={key}
                                                    ref={(el) => {
                                                        if (el) {
                                                            rowRefs.current.set(key, el);
                                                        } else {
                                                            rowRefs.current.delete(key);
                                                        }
                                                    }}
                                                    className={selectedKey === key ? "duplicate-candidates-page__row--selected" : undefined}
                                                >
                                                    <td>
                                                        <button className="action-button" onClick={() => flyTo(c)} disabled={isBusy}>
                                                            Visa
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatAddressLine(c.street1, c.number1)}, {c.city1}
                                                        </div>
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => keepAddress(c, 1)}
                                                            disabled={isBusy}
                                                        >
                                                            {isMerging ? "..." : "Behåll denna"}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {formatAddressLine(c.street2, c.number2)}, {c.city2}
                                                        </div>
                                                        <button
                                                            className="action-button action-button--approve"
                                                            onClick={() => keepAddress(c, 2)}
                                                            disabled={isBusy}
                                                        >
                                                            {isMerging ? "..." : "Behåll denna"}
                                                        </button>
                                                    </td>
                                                    <td>{c.distanceMeters} m</td>
                                                    <td>
                                                        <button
                                                            className="action-button"
                                                            onClick={() => dismissPair(c.id1, c.id2)}
                                                            disabled={isBusy}
                                                        >
                                                            {isDismissing ? "..." : "Inte samma"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </section>
    );
};
